import { Component, type ReactNode } from "react";
import { Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";

/**
 * Route-level error boundary with mid-event deploy recovery.
 *
 * The PWA is autoUpdate: a deploy activates the new service worker
 * immediately and purges the old build's hashed chunks (the CDN 404s them
 * too). Any lazy route a running session hasn't loaded yet then fails to
 * import — previously an unhandled render error and a white screen, on
 * phones staff keep open all shift. Here that specific failure triggers ONE
 * automatic reload (which picks up the new build); a sessionStorage stamp
 * stops a loop when the chunk is missing for another reason (offline,
 * blocked CDN), falling through to the manual UI.
 */

const RELOAD_STAMP_KEY = "sp.chunkReloadAt";
const RELOAD_WINDOW_MS = 60_000;

// Chrome: "Failed to fetch dynamically imported module"; Firefox: "error
// loading dynamically imported module"; Safari: "Importing a module script
// failed"; plus Vite's CSS-preload wrapper error. A wording change in some
// future engine degrades gracefully to the manual Reload button.
const CHUNK_ERROR_RE =
  /failed to fetch dynamically imported module|error loading dynamically imported module|importing a module script failed|unable to preload css|chunkloaderror/i;

function isChunkLoadError(err: unknown): boolean {
  return (
    err instanceof Error &&
    (err.name === "ChunkLoadError" || CHUNK_ERROR_RE.test(err.message))
  );
}

function recentlyReloaded(): boolean {
  try {
    return (
      Date.now() - Number(sessionStorage.getItem(RELOAD_STAMP_KEY) ?? 0) <
      RELOAD_WINDOW_MS
    );
  } catch {
    return true; // storage unavailable — never auto-reload (no loop risk)
  }
}

type State = { error: Error | null };

export class RouteErrorBoundary extends Component<{ children: ReactNode }, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error) {
    if (isChunkLoadError(error) && !recentlyReloaded()) {
      try {
        sessionStorage.setItem(RELOAD_STAMP_KEY, String(Date.now()));
      } catch {
        /* reload anyway; recentlyReloaded() reports true next time */
      }
      window.location.reload();
    }
  }

  render() {
    const { error } = this.state;
    if (!error) return this.props.children;

    const chunk = isChunkLoadError(error);
    if (chunk && !recentlyReloaded()) {
      // componentDidCatch is about to reload — render the same spinner as a
      // route transition so the update is invisible.
      return (
        <div className="flex min-h-screen items-center justify-center bg-background">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </div>
      );
    }

    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-background px-6 text-center">
        <p className="font-heading text-lg">
          {chunk ? "Staff Hub was just updated" : "Something went wrong"}
        </p>
        <p className="max-w-xs text-sm text-muted-foreground">
          {chunk
            ? "Reload to get the latest version."
            : "Try reloading. If it keeps happening, sign out and back in."}
        </p>
        <Button onClick={() => window.location.reload()}>Reload</Button>
      </div>
    );
  }
}
